/**
 * 统一财务管理页面
 * 整合：佣金收入、推荐奖励、提现申请
 */
import { useState, useEffect } from 'react'
import { VITE_API_BASE } from '../config/apiConfig'

const API = VITE_API_BASE

export default function FinancialManagement() {
  const [tab, setTab] = useState('summary') // summary | commission | referral | withdrawal
  const [loading, setLoading] = useState(true)
  
  // 统计数据
  const [stats, setStats] = useState({
    totalCommission: 0,
    totalReferralReward: 0,
    totalWithdrawal: 0,
    pendingWithdrawal: 0,
    availableBalance: 0
  })
  
  // 佣金记录
  const [commissions, setCommissions] = useState([])
  
  // 推荐奖励记录
  const [referrals, setReferrals] = useState([])
  
  // 提现申请记录
  const [withdrawals, setWithdrawals] = useState([])
  
  // 推广员列表（用于筛选）
  const [partners, setPartners] = useState([])
  const [selectedPartner, setSelectedPartner] = useState('')

  useEffect(() => {
    loadAllData()
    loadPartners()
  }, [])

  const loadAllData = async () => {
    setLoading(true)
    try {
      // 并行加载各模块数据
      const [commRes, refRes, wdRes] = await Promise.all([
        fetch(`${API}/partner-commissions/settled`).catch(() => ({ json: () => ({ success: true, data: [] }) })),
        fetch(`${API}/base-referral/list?limit=100`).catch(() => ({ json: () => ({ success: true, data: [], stats: {} }) })),
        fetch(`${API}/regional-agents/withdrawals`).catch(() => ({ json: () => ({ success: true, data: [] }) }))
      ])
      
      const commData = await commRes.json()
      const refData = await refRes.json()
      const wdData = await wdRes.json()
      
      // 处理佣金数据
      const commissions = commData.success ? commData.data || [] : []
      const totalCommission = commissions.reduce((sum, c) => sum + (c.amount || 0), 0)
      
      // 处理推荐奖励数据
      const referrals = refData.success ? refData.data || [] : []
      const totalReferralReward = referrals.reduce((sum, r) => sum + ((r.base_settled ? r.base_reward : 0) + (r.extra_settled ? r.extra_reward : 0)), 0)
      
      // 处理提现数据
      const withdrawals = wdData.success ? wdData.data || [] : []
      const totalWithdrawal = withdrawals.filter(w => w.status === 'approved').reduce((sum, w) => sum + (w.amount || 0), 0)
      const pendingWithdrawal = withdrawals.filter(w => w.status === 'pending').reduce((sum, w) => sum + (w.amount || 0), 0)
      
      setStats({
        totalCommission,
        totalReferralReward,
        totalWithdrawal,
        pendingWithdrawal,
        availableBalance: totalCommission + totalReferralReward - totalWithdrawal
      })
      
      setCommissions(commissions)
      setReferrals(referrals)
      setWithdrawals(withdrawals)
      
    } catch (e) {
      console.error('加载失败', e)
    } finally {
      setLoading(false)
    }
  }

  const loadPartners = async () => {
    try {
      const res = await fetch(`${API}/partners`)
      const data = await res.json()
      if (data.success) {
        setPartners(data.data || [])
      }
    } catch (e) {
      console.error('加载推广员失败', e)
    }
  }

  // 审核提现
  const handleWithdrawal = async (id, status) => {
    if (!confirm(`确定${status === 'approved' ? '批准' : '拒绝'}此提现申请？`)) return
    try {
      const res = await fetch(`${API}/regional-agents/withdrawals/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      })
      const data = await res.json()
      if (data.success) {
        alert(data.message)
        loadAllData()
      }
    } catch (e) {
      alert('操作失败')
    }
  }

  const cardStyle = { background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }

  // 过滤记录
  const filteredCommissions = selectedPartner 
    ? commissions.filter(c => c.partner_id === selectedPartner || c.agent_id === selectedPartner)
    : commissions

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>💰 财务管理</h2>
        <button onClick={loadAllData} style={{ padding: '8px 16px', background: '#1890ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
          🔄 刷新
        </button>
      </div>

      {/* 总览卡片 */}
      {tab === 'summary' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
            <div style={cardStyle}>
              <div style={{ color: '#999', fontSize: 12 }}>佣金总收入</div>
              <div style={{ fontSize: 24, color: '#1890ff', fontWeight: 'bold' }}>¥{stats.totalCommission.toFixed(2)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: '#999', fontSize: 12 }}>推荐奖励</div>
              <div style={{ fontSize: 24, color: '#52c41a', fontWeight: 'bold' }}>¥{stats.totalReferralReward.toFixed(2)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: '#999', fontSize: 12 }}>已提现金额</div>
              <div style={{ fontSize: 24, color: '#722ed1', fontWeight: 'bold' }}>¥{stats.totalWithdrawal.toFixed(2)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: '#999', fontSize: 12 }}>待处理提现</div>
              <div style={{ fontSize: 24, color: '#faad14', fontWeight: 'bold' }}>¥{stats.pendingWithdrawal.toFixed(2)}</div>
            </div>
            <div style={{ ...cardStyle, background: 'linear-gradient(135deg, #1890ff, #52c41a)' }}>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>可提现余额</div>
              <div style={{ fontSize: 24, color: '#fff', fontWeight: 'bold' }}>¥{stats.availableBalance.toFixed(2)}</div>
            </div>
          </div>

          {/* 快速操作 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <button onClick={() => setTab('commission')} style={{ ...cardStyle, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>📊 佣金明细</div>
              <div style={{ color: '#666', fontSize: 13 }}>查看所有推广员佣金记录</div>
              <div style={{ color: '#1890ff', fontSize: 24, fontWeight: 'bold', marginTop: 12 }}>{commissions.length} 笔</div>
            </button>
            <button onClick={() => setTab('referral')} style={{ ...cardStyle, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>🎁 推荐奖励</div>
              <div style={{ color: '#666', fontSize: 13 }}>基地/机构推荐奖励明细</div>
              <div style={{ color: '#52c41a', fontSize: 24, fontWeight: 'bold', marginTop: 12 }}>{referrals.length} 笔</div>
            </button>
            <button onClick={() => setTab('withdrawal')} style={{ ...cardStyle, border: 'none', cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>💸 提现申请</div>
              <div style={{ color: '#666', fontSize: 13 }}>管理提现申请审核</div>
              <div style={{ color: '#faad14', fontSize: 24, fontWeight: 'bold', marginTop: 12 }}>{withdrawals.filter(w => w.status === 'pending').length} 待处理</div>
            </button>
          </div>
        </>
      )}

      {/* 标签页切换 */}
      {tab !== 'summary' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setTab('summary')} style={{ padding: '8px 16px', background: '#f5f5f5', border: 'none', borderRadius: 6, cursor: 'pointer' }}>← 返回总览</button>
            <button onClick={() => setTab('commission')} style={{ padding: '8px 20px', background: tab === 'commission' ? '#1890ff' : '#f5f5f5', color: tab === 'commission' ? '#fff' : '#666', border: 'none', borderRadius: 6, cursor: 'pointer' }}>佣金明细</button>
            <button onClick={() => setTab('referral')} style={{ padding: '8px 20px', background: tab === 'referral' ? '#1890ff' : '#f5f5f5', color: tab === 'referral' ? '#fff' : '#666', border: 'none', borderRadius: 6, cursor: 'pointer' }}>推荐奖励</button>
            <button onClick={() => setTab('withdrawal')} style={{ padding: '8px 20px', background: tab === 'withdrawal' ? '#1890ff' : '#f5f5f5', color: tab === 'withdrawal' ? '#fff' : '#666', border: 'none', borderRadius: 6, cursor: 'pointer' }}>提现记录</button>
          </div>
          <select value={selectedPartner} onChange={e => setSelectedPartner(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 6 }}>
            <option value="">全部推广员</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.name} - {p.phone}</option>)}
          </select>
        </div>
      )}

      {/* 佣金明细 */}
      {tab === 'commission' && (
        <div style={cardStyle}>
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
          : filteredCommissions.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无佣金记录</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>推广员</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>来源</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>订单金额</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>佣金</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>状态</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>时间</th>
                </tr>
              </thead>
              <tbody>
                {filteredCommissions.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 12 }}>{c.partner_name || c.agent_name || '-'}<br/><span style={{ fontSize: 12, color: '#999' }}>{c.partner_phone || c.agent_phone || ''}</span></td>
                    <td style={{ padding: 12 }}>{c.source || '产品销售'}</td>
                    <td style={{ padding: 12, textAlign: 'right' }}>¥{c.order_amount?.toFixed(2) || '0.00'}</td>
                    <td style={{ padding: 12, textAlign: 'right', color: '#52c41a', fontWeight: 'bold' }}>¥{c.amount?.toFixed(2) || '0.00'}</td>
                    <td style={{ padding: 12 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: c.status === 'paid' ? '#f6ffed' : '#fffbe6', color: c.status === 'paid' ? '#52c41a' : '#faad14' }}>
                        {c.status === 'paid' ? '已结算' : '待结算'}
                      </span>
                    </td>
                    <td style={{ padding: 12, color: '#999', fontSize: 12 }}>{c.created_at || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 推荐奖励 */}
      {tab === 'referral' && (
        <div style={cardStyle}>
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
          : referrals.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无推荐记录</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>推荐人</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>被推荐对象</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>基础奖励</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>额外奖励</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>总计</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>状态</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 12 }}>{r.referrer_name}<br/><span style={{ fontSize: 12, color: '#999' }}>{r.referrer_phone}</span></td>
                    <td style={{ padding: 12 }}>{r.base_name}<br/><span style={{ fontSize: 12, color: '#999' }}>{r.phone}</span></td>
                    <td style={{ padding: 12, textAlign: 'right', color: r.base_settled ? '#52c41a' : '#999' }}>
                      ¥{r.base_reward || 200} {r.base_settled ? '✓' : '⏳'}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', color: r.extra_settled ? '#52c41a' : '#999' }}>
                      ¥{r.extra_reward || 300} {r.extra_settled ? '✓' : '⏳'}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 'bold' }}>
                      ¥{((r.base_settled ? r.base_reward : 0) + (r.extra_settled ? r.extra_reward : 0)).toFixed(0)}
                    </td>
                    <td style={{ padding: 12 }}>
                      {r.status === 'pending' && <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#fffbe6', color: '#faad14' }}>待审核</span>}
                      {r.status === 'approved' && <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#e6f7ff', color: '#1890ff' }}>已批准</span>}
                      {r.status === 'rejected' && <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#fff1f0', color: '#ff4d4f' }}>已拒绝</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 提现记录 */}
      {tab === 'withdrawal' && (
        <div style={cardStyle}>
          {loading ? <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
          : withdrawals.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无提现记录</div>
          : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: 12, textAlign: 'left' }}>申请人</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>账户信息</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>申请金额</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>状态</th>
                  <th style={{ padding: 12, textAlign: 'left' }}>申请时间</th>
                  <th style={{ padding: 12, textAlign: 'center' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map(w => (
                  <tr key={w.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: 12 }}>{w.name || '-'}<br/><span style={{ fontSize: 12, color: '#999' }}>{w.phone || ''}</span></td>
                    <td style={{ padding: 12 }}>
                      <div>{w.bank_name || '-'}</div>
                      <div style={{ fontSize: 12, color: '#999' }}>{w.bank_account || ''}</div>
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 'bold', color: '#f5222d' }}>¥{w.amount?.toFixed(2) || '0.00'}</td>
                    <td style={{ padding: 12 }}>
                      {w.status === 'pending' && <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#fffbe6', color: '#faad14' }}>待审核</span>}
                      {w.status === 'approved' && <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#f6ffed', color: '#52c41a' }}>已批准</span>}
                      {w.status === 'rejected' && <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, background: '#fff1f0', color: '#ff4d4f' }}>已拒绝</span>}
                    </td>
                    <td style={{ padding: 12, color: '#999', fontSize: 12 }}>{w.created_at || '-'}</td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      {w.status === 'pending' && (
                        <>
                          <button onClick={() => handleWithdrawal(w.id, 'approved')} style={{ padding: '4px 12px', background: '#52c41a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>批准</button>
                          <button onClick={() => handleWithdrawal(w.id, 'rejected')} style={{ padding: '4px 12px', background: '#ff4d4f', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>拒绝</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
